import React, { useState, useEffect, useMemo } from "react";
import { IonModal, IonIcon } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline, closeOutline } from "ionicons/icons";

interface WeekRangePickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (startDate: string, endDate: string) => void;
  /** YYYY-MM-DD – calendar opens showing this date's month */
  initialDate?: string;
  /** YYYY-MM-DD – earliest selectable date */
  minDate?: string;
  /** YYYY-MM-DD – latest selectable date */
  maxDate?: string;
}

const WEEK_DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const getWeekRange = (date: Date) => {
  // Start week from Monday (getDay() = 1 for Monday)
  const dayOfWeek = date.getDay();
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - mondayOffset);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  return { start: weekStart, end: weekEnd };
};

const WeekRangePicker: React.FC<WeekRangePickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialDate,
  minDate,
  maxDate,
}) => {
  const [viewMonth, setViewMonth] = useState(new Date());
  const [selectedWeek, setSelectedWeek] = useState<{ start: Date; end: Date } | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialDate) {
        const [y, m] = initialDate.split("-").map(Number);
        setViewMonth(new Date(y, m - 1, 1));
        const initialDateObj = new Date(y, m - 1, parseInt(initialDate.split("-")[2]));
        setSelectedWeek(getWeekRange(initialDateObj));
      } else {
        setViewMonth(new Date());
        const today = new Date();
        setSelectedWeek(getWeekRange(today));
      }
    }
  }, [isOpen, initialDate]);

  const todayISO = useMemo(() => toISO(new Date()), []);

  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const firstOfMonth = new Date(year, month, 1);
    // Start from Monday (getDay() = 1 for Monday)
    const dayOfWeek = firstOfMonth.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const start = new Date(firstOfMonth);
    start.setDate(start.getDate() - mondayOffset);

    const days: Date[] = [];
    const cursor = new Date(start);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [viewMonth]);

  const prevMonth = () =>
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1));

  const nextMonth = () =>
    setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1));

  const handleDayClick = (date: Date) => {
    const iso = toISO(date);
    if (minDate && iso < minDate) return;
    if (maxDate && iso > maxDate) return;
    setSelectedWeek(getWeekRange(date));
  };

  const handleGo = () => {
    if (selectedWeek) {
      onSelect(toISO(selectedWeek.start), toISO(selectedWeek.end));
    }
  };

  const formatDisplay = (start: Date, end: Date) => {
    const startStr = start.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const endStr = end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${startStr} - ${endStr}`;
  };

  const isDateInSelectedWeek = (date: Date) => {
    if (!selectedWeek) return false;
    return date >= selectedWeek.start && date <= selectedWeek.end;
  };

  const isDateInCurrentWeek = (date: Date) => {
    const today = new Date();
    const currentWeek = getWeekRange(today);
    return date >= currentWeek.start && date <= currentWeek.end;
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      className="week-range-picker-modal"
      style={
        {
          "--width": "450px",
          "--max-width": "95vw",
          "--height": "auto",
          "--border-radius": "24px",
          "--backdrop-opacity": "0.5",
          "--box-shadow": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
        } as React.CSSProperties
      }
    >
      <div
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
        }}
      >
        {/* Header with Gradient */}
        <div
          style={{
            background: "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)",
            padding: "28px 28px 40px",
            position: "relative",
          }}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.35)";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <IonIcon
              icon={closeOutline}
              style={{ color: "white", fontSize: "20px" }}
            />
          </button>

          {/* Title */}
          <div style={{ textAlign: "center" }}>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "white",
                margin: "0 0 8px",
                letterSpacing: "-0.5px",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              Go to Week
            </h2>
            <p
              style={{
                color: "rgba(255, 255, 255, 0.9)",
                fontSize: "14px",
                margin: 0,
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {selectedWeek ? formatDisplay(selectedWeek.start, selectedWeek.end) : "Select a week to jump to"}
            </p>
          </div>
        </div>

        {/* Content Area */}
        <div
          style={{
            background: "#ffffff",
            marginTop: "-20px",
            borderRadius: "24px 24px 0 0",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Month Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 24px 12px",
            }}
          >
            <button
              onClick={prevMonth}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronBackOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                fontFamily: "Figtree, sans-serif",
              }}
            >
              {viewMonth.toLocaleString("default", { month: "long" })}{" "}
              {viewMonth.getFullYear()}
            </span>
            <button
              onClick={nextMonth}
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e6f9fc";
                e.currentTarget.style.borderColor = "#05acd9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <IonIcon
                icon={chevronForwardOutline}
                style={{ color: "#05acd9", fontSize: "18px" }}
              />
            </button>
          </div>

          {/* Weekday Headers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              padding: "0 20px 4px",
            }}
          >
            {WEEK_DAYS.map((d, i) => (
              <div
                key={i}
                style={{
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#94a3b8",
                  fontFamily: "Figtree, sans-serif",
                }}
              >
                {d}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              padding: "0 20px 16px",
            }}
          >
            {calendarDays.map((date, i) => {
              const iso = toISO(date);
              const inMonth = date.getMonth() === viewMonth.getMonth();
              const isToday = iso === todayISO;
              const inSelectedWeek = isDateInSelectedWeek(date);
              const inCurrentWeek = isDateInCurrentWeek(date);
              const disabled =
                !inMonth ||
                (!!minDate && iso < minDate) ||
                (!!maxDate && iso > maxDate);

              return (
                <button
                  key={i}
                  onClick={() => !disabled && handleDayClick(date)}
                  disabled={disabled}
                  style={{
                    height: "42px",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: inSelectedWeek ? 700 : isToday ? 700 : 400,
                    borderRadius: "50%",
                    border: "none",
                    cursor: disabled ? "default" : "pointer",
                    transition: "all 0.15s ease",
                    fontFamily: "Figtree, sans-serif",
                    color: disabled
                      ? "#d1d5db"
                      : inSelectedWeek
                      ? "#ffffff"
                      : isToday || inCurrentWeek
                      ? "#05acd9"
                      : "#374151",
                    background: inSelectedWeek
                      ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                      : isToday || inCurrentWeek
                      ? "rgba(5, 172, 217, 0.1)"
                      : "transparent",
                    boxShadow: inSelectedWeek
                      ? "0 4px 12px rgba(5, 172, 217, 0.3)"
                      : "none",
                  }}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Go Button */}
          <div
            style={{
              padding: "16px 24px 24px",
              borderTop: "1px solid #f1f5f9",
              background:
                "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            }}
          >
            <button
              onClick={handleGo}
              disabled={!selectedWeek}
              style={{
                width: "100%",
                height: "48px",
                borderRadius: "30px",
                border: "none",
                fontSize: "17px",
                fontWeight: 600,
                color: "white",
                cursor: selectedWeek ? "pointer" : "default",
                fontFamily: "Figtree, sans-serif",
                letterSpacing: "-0.3px",
                transition: "all 0.2s ease",
                background: selectedWeek
                  ? "linear-gradient(135deg, #01b7c4 0%, #00acd7 100%)"
                  : "#cbd5e1",
                boxShadow: selectedWeek
                  ? "0 4px 16px rgba(5, 172, 217, 0.3)"
                  : "none",
                opacity: selectedWeek ? 1 : 0.6,
              }}
            >
              Go
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .week-range-picker-modal {
          --ion-backdrop-opacity: 0.5;
        }
      `}</style>
    </IonModal>
  );
};

export default WeekRangePicker;
